/**
 * Utilities for formatting email HTML and converting ChatGPT Markdown/Pasted Content
 * into clean, delivery-ready, WYSIWYG email HTML.
 */

/**
 * Checks if a string contains Markdown formatting patterns (e.g. from ChatGPT copy button)
 */
export const isMarkdownContent = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  // Patterns common in ChatGPT responses
  const patterns = [
    /^#{1,6}\s+\S+/m,                // Headers: # Header
    /(\*\*|__)(?!\s).+?(?!\s)\1/,    // Bold: **bold**
    /(^|[^\w*])(\*|_)(?!\s).+?(?!\s)\2(?=[^\w*]|$)/, // Italic: *italic*
    /^(\s*[-*+]\s+\S+|\s*\d+\.\s+\S+)/m, // Lists: - item or 1. item
    /^>\s+\S+/m,                     // Blockquote: > quote
    /```[\s\S]*?```/,                // Code block: ```code```
    /`[^`\n]+`/,                     // Inline code: `code`
    /\|[^\n]+\|[^\n]+\|\n\|[\s-:|]+\|/m, // Tables: | col | col |
    /\[.+?\]\(https?:\/\/[^\s)]+\)/, // Links: [title](url)
    /^---{3,}$/m                     // Horizontal rule: ---
  ];

  return patterns.some(pattern => pattern.test(text));
};

/**
 * Converts Markdown (like from ChatGPT) into clean HTML formatted for rich text editors & email.
 */
export const parseMarkdownToHtml = (markdown) => {
  if (!markdown || typeof markdown !== 'string') return '';

  let text = markdown.replace(/\r\n/g, '\n');

  // 1. Code blocks (```language ... ```)
  const codeBlocks = [];
  text = text.replace(/```([a-zA-Z0-9_-]*)\n([\s\S]*?)```/g, (match, lang, code) => {
    const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
    const escapedCode = code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    codeBlocks.push(
      `<pre style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 16px; margin: 12px 0; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 13px; line-height: 1.5; color: #0f172a; overflow-x: auto; white-space: pre-wrap;"><code>${escapedCode}</code></pre>`
    );
    return placeholder;
  });

  // 2. Markdown Tables
  text = text.replace(/(?:^|\n)(\|.+?\|\n\|[\s-:|]+\|\n(?:\|.+?\|\n?)+)/g, (match, tableText) => {
    const lines = tableText.trim().split('\n').filter(Boolean);
    if (lines.length < 2) return match;

    const parseRow = (line) =>
      line
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map(cell => cell.trim());

    const headers = parseRow(lines[0]);
    // Skip index 1 (the separator |---|---|)
    const rows = lines.slice(2).map(parseRow);

    let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 14px; line-height: 1.5;"><thead><tr>';
    headers.forEach(h => {
      tableHtml += `<th style="border: 1px solid #cbd5e1; padding: 8px 12px; background: #f1f5f9; font-weight: 600; text-align: left; color: #0f172a;">${formatInlineMarkdown(h)}</th>`;
    });
    tableHtml += '</tr></thead><tbody>';

    rows.forEach(r => {
      tableHtml += '<tr>';
      r.forEach(cell => {
        tableHtml += `<td style="border: 1px solid #cbd5e1; padding: 8px 12px; color: #334155;">${formatInlineMarkdown(cell)}</td>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody></table>';

    return '\n\n' + tableHtml + '\n\n';
  });

  // 3. Process block by block
  const rawBlocks = text.split(/\n{2,}/);
  const processedBlocks = [];

  for (let block of rawBlocks) {
    block = block.trim();
    if (!block) continue;

    // Restore code blocks if solitary
    if (block.startsWith('__CODE_BLOCK_') && block.endsWith('__')) {
      const idx = parseInt(block.replace('__CODE_BLOCK_', '').replace('__', ''), 10);
      if (codeBlocks[idx]) {
        processedBlocks.push(codeBlocks[idx]);
        continue;
      }
    }

    // HTML Table pass-through
    if (block.startsWith('<table') && block.endsWith('</table>')) {
      processedBlocks.push(block);
      continue;
    }

    // Horizontal Rule
    if (/^(\*\*\*|---|___)$/.test(block)) {
      processedBlocks.push('<hr style="border: none; border-top: 1px solid #e2e8f0; margin: 16px 0;" />');
      continue;
    }

    // Headings (#, ##, ###, ####)
    if (/^#{1,6}\s+/.test(block)) {
      const level = block.match(/^(#{1,6})/)[0].length;
      const headingText = block.replace(/^#{1,6}\s+/, '');
      const styles = {
        1: 'margin: 18px 0 8px 0; font-size: 22px; font-weight: 700; color: #0f172a; line-height: 1.3;',
        2: 'margin: 16px 0 6px 0; font-size: 18px; font-weight: 700; color: #0f172a; line-height: 1.3;',
        3: 'margin: 14px 0 4px 0; font-size: 15px; font-weight: 600; color: #0f172a; line-height: 1.3;',
        4: 'margin: 12px 0 4px 0; font-size: 14px; font-weight: 600; color: #0f172a; line-height: 1.3;',
      };
      const tag = `h${Math.min(level, 4)}`;
      const style = styles[Math.min(level, 4)] || styles[3];
      processedBlocks.push(`<${tag} style="${style}">${formatInlineMarkdown(headingText)}</${tag}>`);
      continue;
    }

    // Blockquotes (> quote)
    if (/^>\s+/.test(block)) {
      const quoteText = block.replace(/^>\s*/gm, '').replace(/\n/g, '<br />');
      processedBlocks.push(
        `<blockquote style="border-left: 4px solid #3b82f6; background: #f8fafc; margin: 12px 0; padding: 8px 16px; color: #475569; font-style: italic; border-radius: 0 6px 6px 0;">${formatInlineMarkdown(quoteText)}</blockquote>`
      );
      continue;
    }

    // Unordered Lists (- item, * item)
    if (/^[-*+]\s+/.test(block)) {
      const items = block.split(/\n(?=[-*+]\s+)/);
      let listHtml = '<ul style="margin: 6px 0 12px 0; padding-left: 22px; line-height: 1.6; color: #1e293b;">';
      items.forEach(item => {
        const cleanItem = item.replace(/^[-*+]\s+/, '').replace(/\n/g, '<br />');
        listHtml += `<li style="margin: 3px 0; line-height: 1.6;">${formatInlineMarkdown(cleanItem)}</li>`;
      });
      listHtml += '</ul>';
      processedBlocks.push(listHtml);
      continue;
    }

    // Ordered Lists (1. item, 2. item)
    if (/^\d+\.\s+/.test(block)) {
      const items = block.split(/\n(?=\d+\.\s+)/);
      let listHtml = '<ol style="margin: 6px 0 12px 0; padding-left: 22px; line-height: 1.6; color: #1e293b;">';
      items.forEach(item => {
        const cleanItem = item.replace(/^\d+\.\s+/, '').replace(/\n/g, '<br />');
        listHtml += `<li style="margin: 3px 0; line-height: 1.6;">${formatInlineMarkdown(cleanItem)}</li>`;
      });
      listHtml += '</ol>';
      processedBlocks.push(listHtml);
      continue;
    }

    // Standard Paragraph with soft line breaks (e.g. signatures or multi-line paragraphs)
    const formattedParagraph = formatInlineMarkdown(block).replace(/\n/g, '<br />');
    processedBlocks.push(`<p style="margin: 0; padding: 0; line-height: 1.5; color: #1e293b;">${formattedParagraph}</p>`);
  }

  // Restore any remaining code block placeholders
  let finalHtml = processedBlocks.join('<p><br></p>');
  codeBlocks.forEach((codeHtml, idx) => {
    finalHtml = finalHtml.replace(`__CODE_BLOCK_${idx}__`, codeHtml);
  });

  return finalHtml;
};

/**
 * Formats inline Markdown (bold, italic, inline code, links)
 */
export const formatInlineMarkdown = (text) => {
  if (!text) return '';

  let out = text;

  // Inline code: `code`
  out = out.replace(/`([^`\n]+)`/g, '<code style="background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-family: ui-monospace, monospace; font-size: 13px; color: #0f172a; border: 1px solid #e2e8f0;">$1</code>');

  // Bold + Italic: ***text***
  out = out.replace(/\*\*\*([^*]+)\*\*\*/g, '<strong><em>$1</em></strong>');

  // Bold: **text** or __text__
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Italic: *text* or _text_
  out = out.replace(/(^|[^*])\*([^*]+)\*(?=[^*]|$)/g, '$1<em>$2</em>');
  out = out.replace(/(^|[^_])_([^_]+)_(?=[^_]|$)/g, '$1<em>$2</em>');

  // Strikethrough: ~~text~~
  out = out.replace(/~~([^~]+)~~/g, '<del>$1</del>');

  // Links: [text](url)
  out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline;">$1</a>');

  return out;
};

/**
 * Safely injects default styles into HTML tags without stripping the tag brackets.
 */
const injectTagStyle = (html, tagName, defaultStyle) => {
  const regex = new RegExp(`<${tagName}(\\s+[^>]*)?>`, 'gi');
  return html.replace(regex, (match, attrs) => {
    if (!attrs || attrs.trim() === '') {
      return `<${tagName} style="${defaultStyle}">`;
    }
    if (/style\s*=\s*"/i.test(attrs)) {
      const newAttrs = attrs.replace(/style\s*=\s*"([^"]*)"/i, (m, oldStyle) => {
        // Prevent appending duplicate style rules if already present
        if (oldStyle.includes(defaultStyle.split(';')[0])) {
          return `style="${oldStyle}"`;
        }
        return `style="${defaultStyle} ${oldStyle}"`;
      });
      return `<${tagName}${newAttrs}>`;
    }
    return `<${tagName} style="${defaultStyle}"${attrs}>`;
  });
};

/**
 * Formats rich HTML for email delivery ensuring 1:1 visual match with ReactQuill editor.
 * Eliminates Gmail's default 80px paragraph spacing and removes unwanted huge gaps in signatures.
 */
export const formatEmailHtmlForDelivery = (rawHtml) => {
  if (!rawHtml || typeof rawHtml !== 'string') return rawHtml;

  // Idempotency: if already wrapped/formatted, return as-is
  if (rawHtml.includes('email-body-wrapper')) {
    return rawHtml;
  }

  let formatted = rawHtml;

  // 1. Collapse multiple consecutive empty paragraphs (<p><br></p>) into a single clean line spacer (16px)
  formatted = formatted.replace(
    /(?:<p[^>]*>\s*(?:<br\s*\/?>|&nbsp;|\s*)\s*<\/p>\s*){2,}/gi,
    '<div style="height: 16px; line-height: 16px; font-size: 14px; margin: 0; padding: 0;">&nbsp;</div>'
  );

  // Single empty Quill paragraph -> clean 16px spacer (single line height)
  formatted = formatted.replace(
    /<p[^>]*>\s*(?:<br\s*\/?>|&nbsp;|\s*)\s*<\/p>/gi,
    '<div style="height: 16px; line-height: 16px; font-size: 14px; margin: 0; padding: 0;">&nbsp;</div>'
  );

  // Clean up any stacked spacer divs so multiple enters never create more than 1 clean line
  formatted = formatted.replace(
    /(?:<div[^>]*height:\s*16px[^>]*>\s*&nbsp;\s*<\/div>\s*){2,}/gi,
    '<div style="height: 16px; line-height: 16px; font-size: 14px; margin: 0; padding: 0;">&nbsp;</div>'
  );

  // 2. Inject styles into HTML tags safely (guaranteeing <tag> is never stripped!)
  // Force left alignment (overrides Quill center/right and pasted web HTML)
  formatted = formatted.replace(/text-align\s*:\s*(center|right|justify)/gi, 'text-align: left');
  formatted = formatted.replace(/\sclass="([^"]*)"/gi, (match, cls) => {
    const cleaned = cls.replace(/\bql-align-(center|right|justify)\b/g, '').replace(/\s+/g, ' ').trim();
    return cleaned ? ` class="${cleaned}"` : '';
  });

  formatted = injectTagStyle(formatted, 'p', 'margin: 0; padding: 0; line-height: 1.5; color: #1e293b; text-align: left;');
  formatted = injectTagStyle(formatted, 'h1', 'margin: 16px 0 6px 0; font-size: 22px; font-weight: 700; color: #0f172a; line-height: 1.3;');
  formatted = injectTagStyle(formatted, 'h2', 'margin: 14px 0 4px 0; font-size: 18px; font-weight: 700; color: #0f172a; line-height: 1.3;');
  formatted = injectTagStyle(formatted, 'h3', 'margin: 12px 0 4px 0; font-size: 15px; font-weight: 600; color: #0f172a; line-height: 1.3;');
  formatted = injectTagStyle(formatted, 'blockquote', 'border-left: 4px solid #3b82f6; background: #f8fafc; margin: 12px 0; padding: 8px 16px; color: #475569; font-style: italic; border-radius: 0 6px 6px 0;');
  formatted = injectTagStyle(formatted, 'pre', 'background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 16px; margin: 12px 0; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 13px; line-height: 1.5; color: #0f172a; overflow-x: auto; white-space: pre-wrap;');
  formatted = injectTagStyle(formatted, 'code', 'background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 13px; color: #0f172a; border: 1px solid #e2e8f0;');
  formatted = injectTagStyle(formatted, 'ul', 'margin: 6px 0 12px 0; padding-left: 22px; line-height: 1.6; color: #1e293b;');
  formatted = injectTagStyle(formatted, 'ol', 'margin: 6px 0 12px 0; padding-left: 22px; line-height: 1.6; color: #1e293b;');
  formatted = injectTagStyle(formatted, 'li', 'margin: 3px 0; line-height: 1.6;');
  formatted = injectTagStyle(formatted, 'table', 'border-collapse: collapse; width: 100%; margin: 12px 0; font-size: 14px; line-height: 1.5;');
  formatted = injectTagStyle(formatted, 'th', 'border: 1px solid #cbd5e1; padding: 8px 12px; background: #f1f5f9; font-weight: 600; text-align: left; color: #0f172a;');
  formatted = injectTagStyle(formatted, 'td', 'border: 1px solid #cbd5e1; padding: 8px 12px; color: #334155;');
  formatted = injectTagStyle(formatted, 'img', 'max-width: 100%; height: auto; border-radius: 6px; display: inline-block; margin: 6px 0;');
  formatted = injectTagStyle(formatted, 'a', 'color: #2563eb; text-decoration: underline; word-break: break-all; overflow-wrap: anywhere;');

  // 3. Wrap inside a clean left-aligned email container constrained to 960px (matching marked boundary)
  if (!formatted.includes('<html') && !formatted.includes('email-body-wrapper')) {
    return `
<div class="email-body-wrapper" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.5; color: #1e293b; text-align: left; max-width: 960px; width: 100%; margin: 0; padding: 0; word-break: break-word; overflow-wrap: break-word; box-sizing: border-box;">
  ${formatted}
</div>
    `.trim();
  }

  return formatted;
};
